import { useCallback, useState } from 'react';
import {
  Box, Stack, TextField, Typography, Button, alpha, Chip, CircularProgress,
} from '@mui/material';
import { Search, CheckCircle } from '@mui/icons-material';
import { lookupCustomerByPhone, createAdminUser } from '../../services/businessService';
import { type CustomerProfile } from './dispatchBookUtils';
import { DISPATCH } from './dispatchTheme';
import PersonNameField from '../ui/forms/PersonNameField';
import PhoneWithDialCodeField from '../ui/forms/PhoneWithDialCodeField';
import { composeE164, localPhoneDigits } from '../../utils/phoneDialCode';
import { validatePersonName, validatePhoneNumber, validateEmail } from '../../utils/personFieldValidation';

export function useDispatchCustomerPhone(initial?: CustomerProfile | null) {
  const [customer, setCustomer] = useState<CustomerProfile | null>(initial || null);
  const [phone, setPhone] = useState(localPhoneDigits(initial?.phone || ''));
  const [countryCode, setCountryCode] = useState('');
  const [firstName, setFirstName] = useState(initial?.firstName || '');
  const [lastName, setLastName] = useState(initial?.lastName || '');
  const [email, setEmail] = useState(initial?.email || '');
  const [lookupState, setLookupState] = useState<'idle' | 'searching' | 'found' | 'new'>(initial?._id ? 'found' : 'idle');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const fullPhone = composeE164(countryCode, phone);

  const handlePhoneChange = useCallback((next: { phone: string; countryCode: string }) => {
    setPhone(next.phone);
    setCountryCode(next.countryCode);
    setLookupState((s) => (s === 'searching' ? s : 'idle'));
    setCustomer(null);
  }, []);

  const handleLookup = async () => {
    const phoneError = validatePhoneNumber(phone, 'Mobile number', { required: true });
    if (phoneError) {
      setFieldErrors({ phone: phoneError });
      setError(phoneError);
      return;
    }
    const digits = localPhoneDigits(phone);
    if (digits.length < 8) {
      setFieldErrors({ phone: 'Enter a valid mobile number' });
      setError('Enter a valid mobile number');
      return;
    }
    setError('');
    setFieldErrors({});
    setLookupState('searching');
    try {
      const user = await lookupCustomerByPhone(fullPhone || digits);
      if (user) {
        setCustomer(user);
        setLookupState('found');
        setFirstName(user.firstName || '');
        setLastName(user.lastName || '');
        setEmail(user.email || '');
      } else {
        setCustomer(null);
        setLookupState('new');
      }
    } catch (e: unknown) {
      setError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Lookup failed');
      setLookupState('idle');
    }
  };

  const resolveCustomerId = async (): Promise<string> => {
    if (customer?._id) return customer._id;
    const digits = localPhoneDigits(phone);
    const next: Record<string, string> = {};
    const phoneError = validatePhoneNumber(phone, 'Mobile number', { required: true });
    if (phoneError) next.phone = phoneError;
    if (digits.length < 8) next.phone = 'Valid phone number required';
    const nameError = validatePersonName(firstName, 'First name', { required: true });
    if (nameError) next.firstName = nameError;
    const lastError = validatePersonName(lastName, 'Last name');
    if (lastError) next.lastName = lastError;
    const emailError = validateEmail(email, 'Email', { required: true });
    if (emailError) next.email = emailError;
    if (Object.keys(next).length) {
      setFieldErrors(next);
      throw new Error(Object.values(next)[0]);
    }
    const user = await createAdminUser({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: fullPhone || digits,
      email: email.trim(),
      userType: 'customer',
    });
    setCustomer(user);
    return user._id;
  };

  const reset = () => {
    setCustomer(null);
    setPhone('');
    setCountryCode('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setLookupState('idle');
    setError('');
    setFieldErrors({});
  };

  const found = lookupState === 'found' && !!customer;
  const detailsLocked = found;

  const panel = (
    <Box sx={{
      bgcolor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '10px',
      px: 1.5,
      py: 1,
      flexShrink: 0,
    }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
        <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.1em', color: '#64748b', fontSize: '0.68rem' }}>PASSENGER</Typography>
        {found && (
          <Chip size="small" icon={<CheckCircle />} label="Existing customer" sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem', bgcolor: alpha(DISPATCH.success, 0.1), color: DISPATCH.success, '& .MuiChip-icon': { color: DISPATCH.success, fontSize: 14 } }} />
        )}
        {lookupState === 'new' && (
          <Chip size="small" label="New customer" sx={{ height: 22, fontWeight: 700, fontSize: '0.68rem', bgcolor: alpha(DISPATCH.warning, 0.12), color: '#b45309' }} />
        )}
      </Stack>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'nowrap', minWidth: 0 }}>
        <Box sx={{ width: 250, flexShrink: 0, minWidth: 190 }}>
            <PhoneWithDialCodeField
              layout="inline"
              hideHelper
              preferredCountry="IN"
              label="Mobile number"
              phone={phone}
              countryCode={countryCode}
              onChange={handlePhoneChange}
              required
              error={!!fieldErrors.phone}
              helperText={fieldErrors.phone}
              onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            />
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={lookupState === 'searching' ? <CircularProgress size={14} color="inherit" /> : <Search />}
          onClick={handleLookup}
          disabled={lookupState === 'searching' || localPhoneDigits(phone).length < 8}
          sx={{
            height: 36,
            flexShrink: 0,
            borderRadius: '8px',
            px: 1.75,
            textTransform: 'none',
            fontWeight: 800,
            bgcolor: '#0f172a',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#1e293b', boxShadow: 'none' },
          }}
        >
          {lookupState === 'searching' ? 'Finding…' : 'Find'}
        </Button>
        <PersonNameField
          size="small"
          label="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          required
          disabled={detailsLocked}
          error={!!fieldErrors.firstName}
          sx={{ flex: 1, minWidth: 110 }}
        />
        <PersonNameField
          size="small"
          label="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          disabled={detailsLocked}
          error={!!fieldErrors.lastName}
          sx={{ flex: 1, minWidth: 110 }}
        />
        <TextField
          size="small"
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={detailsLocked}
          error={!!fieldErrors.email}
          sx={{ flex: 1.2, minWidth: 140 }}
        />
      </Stack>
      {error && (
        <Typography variant="caption" color="error" fontWeight={700} sx={{ display: 'block', mt: 0.75 }}>
          {error}
        </Typography>
      )}
    </Box>
  );

  return {
    panel,
    customer,
    isReady: lookupState === 'found' || lookupState === 'new',
    resolveCustomerId,
    reset,
  };
}
